/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';
import { CQReactTable } from '@laxmimanogna/code-quick-components';
import { UrgentIcon } from '../../../../constants/IconData';

import { createColumnHelper } from '@tanstack/react-table';
import {
  getAuditStatus,
  MY_AUDITS_ORDERING,
} from '../../../../constants/constants';
import useCQMediaQuery from '../../../../hooks/useCQMediaQuery';
import { format } from 'date-fns';

import '../style.css';
import { MyAuditContext } from '../../../../providers/MyAuditProvider';
import { renderActionComponent } from '../MyAuditsComponents';
import {
  createTableProps,
  paginationProps,
} from '../../../../utils/my_audits.utils';
import { useNavigate } from 'react-router-dom';
import { toTitleCase } from '../../../../utils/common.utils';

const columnHelper = createColumnHelper();

const MyArchivedTable = () => {
  const myAuditContext = useContext(MyAuditContext);
  const navigate = useNavigate();
  const [ordering, setOrdering] = useState('');
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  // media query
  const canScroll = useCQMediaQuery({
    lg: false,
    md: false,
    sm: true,
  });

  const { isUpdating, getAuditArchived, filterParams } = myAuditContext;

  // SCROLL CHANGE: Auto-restore
  const PAGE_NAME = "My Audits";
  const TABLE_NAME = "Archived";

  useEffect(() => {
    if (myAuditContext.auditArchivedList.length > 0) {
      const itemId = getAuditArchived.restoreNavigationState(PAGE_NAME, TABLE_NAME, true);
      if (itemId) {
        setHighlightedRowId(itemId);
        setTimeout(() => {
          setHighlightedRowId(null);
        }, 2000);
      }
    }
  }, [myAuditContext.auditArchivedList]);

  useEffect(() => {
    let searching = !!(filterParams.searchText && filterParams.searchText.trim());
    getAuditArchived.loadAPI("My Audit", "My Archived", searching);
  }, [filterParams, getAuditArchived.showOrdering]);

  useEffect(() => {
    if (MY_AUDITS_ORDERING.includes(ordering)) {
      getAuditArchived.setParams({ ordering });
    }
  }, [ordering]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('upload_id', {
        header: () => 'Upload #',
        id: 'chart_id',
        cell: info => {
          return (
            <HStack
              justifyContent={'flex-start'}
              alignItems={'center'}
              spacing={10}
            >
              {info.row.original?.urgent ? <UrgentIcon /> : <Box ml={2} />}
              <Text>{info.renderValue()}</Text>
            </HStack>
          );
        },
      }),
      columnHelper.accessor(row => row.uploaded_date, {
        id: 'upload_date',
        header: () => <span>Upload Date</span>,
        cell: info => {
          const cellValue = info.getValue();
          if (!cellValue) {
            return 'N/A';
          }

          return format(new Date(cellValue), 'MM/dd/yyyy');
        },
      }),
      columnHelper.accessor(row => row.total_page, {
        id: 'total_pages',
        header: () => <span>Pages</span>,
        cell: info => {
          const cellValue = info.getValue();
          if (!cellValue) {
            return 'N/A';
          }

          return cellValue;
        },
      }),
      columnHelper.accessor(p => p.specialties?.name, {
        header: () => 'Specialties',
        id: 'specialty__name',
        cell: info => {
          const specialties = info.row.original.specialties?.name;

          if (!specialties) {
            return 'N/A';
          }
          return specialties === 'ent' ? 'ENT' : toTitleCase(specialties);
        },
      }),

      columnHelper.accessor('status', {
        header: () => 'Status',
        id: 'status',
        cell: info => <Text>{getAuditStatus(info.row.original?.status)}</Text>,
      }),

      columnHelper.accessor(row => row, {
        id: 'actions',
        header: () => 'Actions',
        enableSorting: false,
        cell: info => {
          const obj = info.getValue();
          return renderActionComponent(obj, navigate, myAuditContext, getAuditArchived, PAGE_NAME, TABLE_NAME);
        },
      }),
    ],
    [myAuditContext.isAuditUploadListLoading]
  );

  function handlePreviousPage() {
    myAuditContext.getAuditArchived.loadPrevious("My Audit", "My Archived");
  }

  function handleNextPage() {
    myAuditContext.getAuditArchived.loadNext("My Audit", "My Archived");
  }

  function handleSizeChange(size) {
    myAuditContext.getAuditArchived.loadSize("My Audit", "My Archived", size );
  }

  function handlePageChangeByNumber(pageNumber) {
    myAuditContext.getAuditArchived.loadPageByNumber("My Audit", "My Archived", pageNumber);
  }

  return (
    <Box
      pointerEvents={isUpdating ? 'none' : 'inherit'}
      overflowX={canScroll ? 'scroll' : 'inherit'}
    >
      <CQReactTable
        manualSortBy={true}
        isLoading={myAuditContext.isAuditUploadListLoading}
        data={myAuditContext.auditArchivedList}
        columns={columns}
        {...createTableProps(highlightedRowId)}
        {...paginationProps(getAuditArchived)}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onSizeChange={handleSizeChange}
        onPageChangeByNumber={handlePageChangeByNumber}
        onAsc={header => {
          setOrdering(header);
        }}
        onDesc={header => {
          setOrdering(`-${header}`);
        }}
      />
    </Box>
  );
};
export default MyArchivedTable;
